import { SelectedMessage } from '@hb/shared'
import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

export const useThreadScroll = (
  scrollContainerRef: RefObject<HTMLDivElement>,
  // maxScroll: RefObject<number>,
  onScroll: (scrollTop: number, direction: 'up' | 'down') => void,
) => {
  const [scrollTop, setScrollTop] = useState(0)

  // Function to scroll to a specific position
  const scrollTo = useCallback(
    (position: number, behavior: ScrollBehavior = 'smooth') => {
      const container = scrollContainerRef.current
      if (!container) return

      const clampedPosition = Math.max(
        0,
        Math.min(position, container.scrollHeight - container.clientHeight),
      )
      container.scrollTo({ top: clampedPosition, behavior })
    },
    [scrollContainerRef],
  )

  // Function to scroll by a delta amount
  const scrollBy = useCallback(
    (delta: number, behavior: ScrollBehavior = 'smooth') => {
      const container = scrollContainerRef.current
      if (!container) return

      container.scrollBy({ top: delta, behavior })
    },
    [scrollContainerRef],
  )

  // Instant scroll without animation
  const scrollToInstant = useCallback(
    (position: number) => scrollTo(position, 'instant'),
    [scrollTo],
  )

  const scrollByInstant = useCallback((delta: number) => scrollBy(delta, 'instant'), [scrollBy])

  const initialized = useRef(false)
  const prevScrollTop = useRef(0)
  // Set up scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const newScrollTop = container.scrollTop
      const direction = newScrollTop > prevScrollTop.current ? 'down' : 'up'
      prevScrollTop.current = newScrollTop
      setScrollTop(newScrollTop)
      onScroll(newScrollTop, direction)
    }

    // Initial calculation
    if (!initialized.current) {
      initialized.current = true
    }

    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [scrollContainerRef, onScroll])

  return {
    scrollTop,
    scrollTo,
    scrollBy,
    scrollToInstant,
    scrollByInstant,
  }
}

export const useScrollToMessage = (
  selectedMessage: SelectedMessage | null | undefined,
  loading: boolean,
  scrollContainerRef: RefObject<HTMLDivElement>,
  scrollTo: (position: number, behavior?: ScrollBehavior) => void,
) => {
  const lastScrolledToSelectedOn = useRef<number | null>(null)

  const scrollToMessage = useCallback(
    (message: SelectedMessage) => {
      const container = scrollContainerRef.current
      if (!container) return false

      // Find the message element by ID
      const messageElement = document.getElementById(`message-${message.messageId}`)
      if (!messageElement) return false

      scrollTo(messageElement.offsetTop)
      return true
    },
    [scrollTo, scrollContainerRef],
  )

  useLayoutEffect(() => {
    if (
      !selectedMessage ||
      selectedMessage.selectedOn === lastScrolledToSelectedOn.current ||
      !scrollContainerRef.current ||
      loading
    ) {
      return
    }

    if (scrollToMessage(selectedMessage)) {
      lastScrolledToSelectedOn.current = selectedMessage.selectedOn
    }
  }, [selectedMessage, scrollContainerRef, scrollToMessage, loading])

  return scrollToMessage
}
